import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  SwipeableDrawer,
  Typography,
  IconButton,
  Button,
  Grid,
} from '@mui/material'
import Dinero from 'dinero.js'
import { useForm } from 'react-hook-form'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { useDispatch, useSelector } from 'react-redux'
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { randomId } from '@mui/x-data-grid-generator'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalCustomActionBar from 'components/generic/modals/form-modal/FormModalCustomActionBar'
import BasicDataGrid from 'components/generic/datagrid'
import MaterialAutocomplete from 'components/generic/form/AsyncAutocomplete/MaterialAutocomplete'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import EditableCell from 'components/generic/datagrid/cells/EditableCell'
import { setSnackBar } from 'state/ui'
import { calcAdjustments, setDecimalPrecision } from 'utils/helpers'
import { getNetValue } from 'utils/data-manipulation/common'
import { IBT_VIEW_OPTIONS, MEASURE_UNIT, ROUNDING_PRECISIONS } from 'utils/constants'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import {
  calcTotalPercentage,
  getActionBarButtons,
  splitOptions,
  SPLIT_WEIGHT_OPTIONS,
  useStyles,
  formatMaterials,
} from './utils'

const SplitTruckAndMaterialDifferentialBlade = ({
  differentialAmount,
  openBlade,
  setOpenBlade,
  materials,
  onSave,
  viewMode,
  purchaseOrderData,
}) => {
  const classes = useStyles()
  const methods = useForm()
  const { handleSubmit, control } = methods
  const [optionSelected, setOptionSelected] = useState(SPLIT_WEIGHT_OPTIONS.EXISTING_MATERIALS)
  const { defaultCurrency, defaultYardUm, settings } = useSelector(state => state.settings)
  const { showWeightDecimals } = settings?.purchaseSettings?.inboundTickets || {}
  const [isValid, setIsValid] = useState(false)
  const dispatch = useDispatch()
  const materialsApiRef = useGridApiRef()
  const weightAndPrice = {
    gross: { amount: 0, units: defaultYardUm },
    tare: { amount: 0, units: defaultYardUm },
    deductions: [],
    netPrice: { amount: 0, units: defaultYardUm },
    netValue: { amount: 0, units: defaultYardUm },
    netWeight: { amount: 0, units: defaultYardUm },
    um: defaultYardUm,
  }
  const filteredMaterials = useMemo(
    () => materials?.filter(material => material.materialCode !== 'scrap-vehicle') || [],
    [materials],
  )
  const handleChangeSplitOption = e => {
    setIsValid(false)
    setOptionSelected(e.target.value)
  }

  const focusInput = (rowId, fieldName) => {
    const cell = materialsApiRef.current.getCellElement(rowId, fieldName)
    const input = cell?.getElementsByTagName('input')
    if (input && input[0]) input[0]?.focus()
  }

  const handleError = error => {
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.data?.error || 'There was an error.',
      }),
    )
  }

  const validatePercentageTotal = () => {
    const allItems = Array.from(materialsApiRef.current.getRowModels().values())
    const total = calcTotalPercentage(allItems)
    if (total > 100) {
      handleError({ data: { error: 'Percentage is greater than 100%' } })
    }
    setIsValid(true)
  }

  const handleChangePercentage = (event, row) => {
    setIsValid(false)
    materialsApiRef.current.updateRows([
      {
        ...row,
        percentage: parseInt(event.target.value, 10) || 0,
      },
    ])
    validatePercentageTotal()
  }

  const handleDeleteClick = useCallback(
    id => event => {
      setIsValid(false)
      event.stopPropagation()
      materialsApiRef.current?.updateRows([{ id, _action: 'delete' }])
      validatePercentageTotal()
    },
    [materialsApiRef],
  )

  const onClickNewMaterial = useCallback(() => {
    setIsValid(false)
    const id = randomId()
    materialsApiRef.current?.updateRows([
      {
        id,
        isNew: true,
        percentage: 0,
        tare: 0,
        rowIndex: materialsApiRef.current?.getRowsCount() + 1,
        ...(viewMode === IBT_VIEW_OPTIONS.SCALE ? { weightAndPrice } : null),
      },
    ])

    materialsApiRef.current?.scrollToIndexes({
      rowIndex: materialsApiRef.current?.getRowsCount() - 1,
    })
    validatePercentageTotal()
  }, [materialsApiRef])

  const includeOriginalMaterials = () => {
    if (optionSelected === SPLIT_WEIGHT_OPTIONS.NEW_MATERIALS) {
      const materialsUpdatedApiRef = []
      const currentMaterials = Array.from(materialsApiRef.current.getRowModels().values())
      const originalMaterials = materials
      originalMaterials.forEach(originMat => {
        const materialFound = currentMaterials.find(
          item =>
            item.materialTypeId === originMat.materialTypeId &&
            item.id === originMat.id &&
            item.orderEntryId === originMat.orderEntryId,
        )
        const rowIndex = materialsUpdatedApiRef.length + 1
        materialsUpdatedApiRef.push({
          ...(materialFound || originMat),
          rowIndex,
        })
      })

      let cont = 0
      currentMaterials.forEach(currentMat => {
        const materialWasAdded = materialsUpdatedApiRef.find(
          item =>
            item.materialTypeId === currentMat.materialTypeId &&
            item.id === currentMat.id &&
            item.orderEntryId === currentMat.orderEntryId,
        )

        if (!materialWasAdded) {
          const id = randomId()
          cont++
          materialsApiRef.current.updateRows([
            { id: currentMat.id, _action: 'delete' },
            {
              ...currentMat,
              id,
              rowIndex: materialsUpdatedApiRef.length + cont,
            },
          ])
        }
      })
      materialsApiRef.current.updateRows(materialsUpdatedApiRef)
    }
  }

  const onSubmit = () => {
    const allItems = Array.from(materialsApiRef.current.getRowModels().values())
    allItems.forEach(row => {
      const { percentage = 0, um: rowUm } = row
      const proportionedDifferential = (differentialAmount * percentage) / 100

      if (viewMode === IBT_VIEW_OPTIONS.SCALE) {
        const { gross, tare, netWeight, deductions } = row.weightAndPrice
        const priceAmount = roundTo(Number(row.price) * 10 ** 4)
        const previousGrossAmount = Number(gross.amount) || 0
        const newGrossAmount = roundTo(
          previousGrossAmount + proportionedDifferential,
          showWeightDecimals ? ROUNDING_PRECISIONS.WEIGHT : 0,
        )
        const tareAmount = Number(tare.amount) || 0

        // NOTE: netValueAmount here is 4 orders of magnitude larger because of how we are passing in the price
        const netValueAmount = getNetValue({
          gross: newGrossAmount,
          tare: tareAmount,
          price: Number(priceAmount) || 0,
          deductions,
          fromUnit: defaultYardUm,
          toUnit: rowUm,
        })
        const { weightAdjustments } = calcAdjustments({
          weightAndPrice: { gross: { amount: newGrossAmount }, tare: { amount: tareAmount }, deductions },
        })
        const netWeightAmount = roundTo(
          newGrossAmount - tareAmount - weightAdjustments,
          showWeightDecimals ? ROUNDING_PRECISIONS.WEIGHT : 0,
        )
        const rest = row.isNew
          ? {
              netPrice: {
                amount: priceAmount,
                precision: 4,
                currency: defaultCurrency,
              },
              customerPreferredPrice: {
                amount: priceAmount,
                precision: 4,
                currency: defaultCurrency,
              },
              netWeight: {
                amount: netWeightAmount,
                units: row.weightAndPrice?.netWeight?.units,
              },
              tare: {
                amount: tareAmount,
                units: row.weightAndPrice?.tare?.units,
              },
              um: rowUm,
              customerPreferredUm: rowUm,
            }
          : null
        materialsApiRef.current.updateRows([
          {
            ...row,
            weightAndPrice: {
              ...row.weightAndPrice,
              gross: {
                ...gross,
                amount: newGrossAmount,
              },
              netValue: {
                amount: roundTo(netValueAmount),
                precision: 4,
                currency: defaultCurrency,
              },
              netWeight: {
                ...netWeight,
                amount: netWeightAmount,
              },
              ...rest,
            },
            percentage: 0,
          },
        ])
      } else if (viewMode === IBT_VIEW_OPTIONS.INDUSTRIAL) {
        const { gross, tare, price, deductions = [] } = row
        const newGross = roundTo(
          (gross || 0) + proportionedDifferential,
          showWeightDecimals ? ROUNDING_PRECISIONS.WEIGHT : 0,
        )
        const newNetValue = getNetValue({
          gross: newGross,
          tare: tare || 0,
          price: Number(price) || 0,
          deductions,
          fromUnit: defaultYardUm,
          toUnit: rowUm,
        })
        const { weightAdjustments } = calcAdjustments({
          weightAndPrice: { gross: { amount: newGross }, tare: { amount: tare }, deductions },
        })
        const newNetWeight = roundTo(Number(newGross) - Number(tare) - weightAdjustments, ROUNDING_PRECISIONS.WEIGHT)
        materialsApiRef.current.updateRows([
          {
            ...row,
            gross: newGross,
            netValue: newNetValue,
            netWeight: newNetWeight,
            percentage: 0,
          },
        ])
      }
    })
    includeOriginalMaterials()
    setOpenBlade(false)
    onSave(
      formatMaterials(
        Array.from(materialsApiRef.current.getRowModels().values()).sort((a, b) => {
          if (!a.rowIndex) return 1
          if (!b.rowIndex) return -1
          return a.rowIndex - b.rowIndex
        }),
      ),
    )
  }

  const materialColumns = [
    {
      field: 'materialTypeId',
      minWidth: 175,
      flex: 2,
      renderCell: params => {
        const { row } = params
        return (
          <MaterialAutocomplete
            variant="outlined"
            size="small"
            label="Material"
            defaultValue={{
              commonName: row.materialType?.commonName,
              code: row.materialType?.code,
              materialTypeId: row.materialTypeId,
              orderEntryId: row.orderEntryId,
            }}
            InputProps={{
              disabled: optionSelected === SPLIT_WEIGHT_OPTIONS.EXISTING_MATERIALS,
            }}
            TextFieldProps={{
              error: !row.materialTypeId,
            }}
            disabled={optionSelected === SPLIT_WEIGHT_OPTIONS.EXISTING_MATERIALS}
            onFocus={event => setTimeout(() => event.target.focus(), 50)}
            onChange={(value, reason) => {
              if (!value) {
                setIsValid(false)
                return
              }
              const isFirstLoad = reason === undefined
              const { materialTypeId: rowMaterialTypeId, gross, tare, deductions, netWeight } = row
              const {
                code,
                id: materialTypeId,
                commonName,
                defaultPriceEntry,
                supplementalPriceEntry,
                orderEntryId,
                overrideName,
              } = value
              let netPrice = null
              if (rowMaterialTypeId === materialTypeId) {
                const material = materials.find(item => item.id === row.id)
                netPrice = material?.netPrice || Dinero({ ...material?.weightAndPrice?.netPrice }).toUnit() || null
              } else {
                netPrice = Dinero({ ...value?.defaultPriceEntry?.buyPrice?.price }).toUnit()
              }

              const priceEntry = supplementalPriceEntry || defaultPriceEntry

              const defaultPrice =
                priceEntry && typeof priceEntry?.buyPrice?.price === 'object'
                  ? Dinero({ ...priceEntry?.buyPrice?.price }).toUnit()
                  : Number(priceEntry?.buyPrice?.price) || 0

              // if isFirstLoad = true, then material is already on the IBT so use "netPrice"
              // otherwise, user is changing the material so use the cascaded default price (PO > price list > default price sheet)
              const materialPrice = isFirstLoad && (netPrice || netPrice === 0) ? netPrice : defaultPrice
              const newUm = priceEntry.weightUnits

              const newNetValue = getNetValue({
                gross: rowMaterialTypeId === materialTypeId ? gross : 0,
                tare: rowMaterialTypeId === materialTypeId ? tare : 0,
                price: Number(materialPrice) || 0,
                deductions,
                fromUnit: defaultYardUm,
                toUnit: newUm,
              })
              materialsApiRef.current.updateRows([
                {
                  ...row,
                  code,
                  materialCode: code,
                  gross: rowMaterialTypeId === materialTypeId ? gross : 0,
                  tare: rowMaterialTypeId === materialTypeId ? tare : 0,
                  netWeight: rowMaterialTypeId === materialTypeId ? netWeight : 0,
                  materialTypeId,
                  orderEntryId,
                  overrideName,
                  materialType: null,
                  materialName: commonName || '',
                  um: newUm,
                  price: setDecimalPrecision(materialPrice, {
                    shouldFormatNumber: false,
                  }),
                  netValue: newNetValue,
                  isNew: rowMaterialTypeId !== materialTypeId,
                  customerPreferredUm: newUm,
                  customerPreferredPrice: setDecimalPrecision(materialPrice, {
                    shouldFormatNumber: false,
                  }),
                  defaultPriceEntry,
                  overridePriceEntry: supplementalPriceEntry,
                },
              ])
              validatePercentageTotal()
            }}
            canAutoSelect
            orderData={purchaseOrderData}
          />
        )
      },
      sortable: false,
    },
    {
      field: 'percentage',
      minWidth: 115,
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: params => {
        const { row = {} } = params
        const { id, percentage } = row
        return (
          <EditableCell
            control={control}
            name={`${id}.percentage`}
            defaultValue={percentage}
            isEdit
            size="small"
            label="Percentage"
            InputProps={{
              inputComponent: InputFormat,
              endAdornment: (
                <InputAdornment position="end" onClick={() => focusInput(id, 'percentage')}>
                  %
                </InputAdornment>
              ),
              inputProps: {
                onChange: event => handleChangePercentage(event, row),
                onFocus: event => setTimeout(() => event.target.focus(), 50),
                name: 'percentage',
                inputType: 'number',
                decimalScale: 0,
                allowNegative: false,
                style: {
                  fontSize: '0.875rem',
                  textAlign: 'right',
                  width: '100px',
                },
              },
            }}
          />
        )
      },
      sortable: false,
    },
    {
      field: 'actions',
      minWidth: 100,
      flex: 1,
      headerName: '',
      renderCell: params => {
        const { row = {} } = params
        const { id } = row
        if (optionSelected === SPLIT_WEIGHT_OPTIONS.EXISTING_MATERIALS) return null
        return (
          <div>
            <IconButton
              size="small"
              sx={{
                fill: 'red',
                width: '40px',
                height: '40px',
              }}
              aria-label="delete"
              onClick={handleDeleteClick(id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        )
      },
      headerAlign: 'center',
      align: 'left',
      sortable: false,
      width: 20,
    },
  ]

  return (
    <SwipeableDrawer
      anchor="right"
      open={openBlade}
      onClose={() => setOpenBlade(false)}
      onOpen={() => setOpenBlade(true)}
      PaperProps={{ sx: { maxWidth: '1024px', width: '100%' } }}
      disableSwipeToOpen
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack height="100vh">
          <FormModalHeading
            title="Split Truck and Material Differential"
            backButtonPosition="right"
            onBack={() => setOpenBlade(false)}
          />
          <Box mt={1} mb={3}>
            <Divider />
          </Box>
          <Stack px={4} flexGrow={1}>
            <Typography width="80%">
              When the gross weight of the truck is more than the weight of the materials on your ticket, you have two
              options.You can either distribute the extra weight among the existing materials on your ticket, or you can
              add new materials to your ticket and distribute the weight among them.
            </Typography>
            <FormControl>
              <RadioGroup
                defaultValue={SPLIT_WEIGHT_OPTIONS.EXISTING_MATERIALS}
                name="radio-buttons-group"
                value={optionSelected}
                onChange={handleChangeSplitOption}
              >
                {splitOptions.map(option => (
                  <Stack key={option?.id} data-testid={option?.id}>
                    <FormControlLabel
                      value={option?.id}
                      control={<Radio />}
                      label={option?.name}
                      sx={{ width: 'fit-content' }}
                    />
                    {option?.id === optionSelected ? (
                      <Grid width="70%">
                        <BasicDataGrid
                          className={classes.root}
                          columns={materialColumns}
                          rows={filteredMaterials}
                          apiRef={materialsApiRef}
                          hideFooter
                          headerHeight={0}
                          height="auto"
                          editMode="row"
                        />
                        {option?.id === SPLIT_WEIGHT_OPTIONS.NEW_MATERIALS ? (
                          <Box paddingLeft={4.5}>
                            <Button
                              startIcon={<AddIcon />}
                              onClick={onClickNewMaterial}
                              data-testid="split-material-new-button"
                              sx={{
                                justifyContent: 'left',
                              }}
                            >
                              Add New Material
                            </Button>
                          </Box>
                        ) : null}
                      </Grid>
                    ) : null}
                  </Stack>
                ))}
              </RadioGroup>
            </FormControl>
          </Stack>
          <FormModalCustomActionBar buttonConfigs={getActionBarButtons(setOpenBlade, isValid)} justify="flex-start" />
        </Stack>
      </form>
    </SwipeableDrawer>
  )
}

SplitTruckAndMaterialDifferentialBlade.propTypes = {
  differentialAmount: PropTypes.number.isRequired,
  openBlade: PropTypes.bool,
  setOpenBlade: PropTypes.func,
  materials: PropTypes.array,
  onSave: PropTypes.func,
  viewMode: PropTypes.string.isRequired,
  purchaseOrderData: PropTypes.object,
}

SplitTruckAndMaterialDifferentialBlade.defaultProps = {
  openBlade: false,
  setOpenBlade: () => {},
  materials: [],
  onSave: () => {},
  purchaseOrderData: null,
}

export default SplitTruckAndMaterialDifferentialBlade
